import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';

// Interval (gap) between digits in ms
const SPEED_CONFIG = {
  slow: 300,   // 250ms gap after each digit (slow)
  fast: 50,    // 50ms gap after each digit (fast)
};

// ============== Web Audio API for consistent, glitch-free playback ==============
// AudioContext + decoded AudioBuffers give sample-accurate timing.
// Unlike HTMLAudioElement, there are no async onended delays or browser throttling.
let audioCtx = null;
const digitBuffers = {}; // { 1: AudioBuffer, 2: AudioBuffer, ... }
let audioLoaded = false;
let activeSource = null; // Track current playing source for stop

const ensureAudioContext = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume if suspended (browser autoplay policy)
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

// Fetch and decode all digit WAV files into AudioBuffers
const loadAllDigitBuffers = async () => {
  if (audioLoaded) return;
  const ctx = ensureAudioContext();
  const loadPromises = [];
  for (let d = 1; d <= 9; d++) {
    loadPromises.push(
      fetch(`/audio/rms/${d}.wav`)
        .then(res => res.arrayBuffer())
        .then(buf => ctx.decodeAudioData(buf))
        .then(decoded => { digitBuffers[d] = decoded; })
        .catch(() => { /* digit will be skipped if load fails */ })
    );
  }
  await Promise.all(loadPromises);
  audioLoaded = true;
};

// Initialize audio on user interaction (iOS requirement)
const warmUpAudio = () => {
  ensureAudioContext();
  loadAllDigitBuffers();
};

// Stop any currently playing audio
const stopAllAudio = () => {
  if (activeSource) {
    try { activeSource.stop(); } catch (e) { /* already stopped */ }
    activeSource = null;
  }
};

// Play a digit using Web Audio API.
// Returns a Promise that resolves after the buffer's exact duration.
// Uses setTimeout based on the known buffer duration — NOT onended events.
const playDigit = (digit) => {
  return new Promise((resolve) => {
    const ctx = ensureAudioContext();
    const buffer = digitBuffers[digit];
    if (!buffer) { resolve(); return; }
    
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    activeSource = source;
    source.start(0);
    
    // Resolve after the buffer's exact duration (ms precision)
    // This is deterministic — no browser event loop jitter
    const durationMs = Math.ceil(buffer.duration * 1000);
    setTimeout(() => {
      activeSource = null;
      resolve();
    }, durationMs);
  });
};

const TOTAL_ROUNDS = 25;
const MIN_SEQUENCE_LENGTH = 6;
const MAX_SEQUENCE_LENGTH = 25;
const MAX_DELETES = 2; // Can delete up to 2 of the most recently entered digits

// Generate a random sequence of digits 1-9 with no consecutive duplicates
const generateSequence = () => {
  const length = Math.floor(Math.random() * (MAX_SEQUENCE_LENGTH - MIN_SEQUENCE_LENGTH + 1)) + MIN_SEQUENCE_LENGTH;
  const sequence = [];
  for (let i = 0; i < length; i++) {
    let digit;
    do {
      digit = Math.floor(Math.random() * 9) + 1; // 1-9
    } while (sequence.length > 0 && digit === sequence[sequence.length - 1]);
    sequence.push(digit);
  }
  return sequence;
};

// Score: compare user input against reversed sequence from first digit forward until first error
// e.g. sequence "123456", reversed "654321", user enters "654X21" → score is 3 (first 3 correct)
const calculateScore = (sequence, userInput) => {
  const reversed = [...sequence].reverse();
  let score = 0;
  for (let i = 0; i < Math.min(userInput.length, reversed.length); i++) {
    if (parseInt(userInput[i]) === reversed[i]) {
      score++;
    } else {
      break;
    }
  }
  return score;
};


const RMSGame = () => {
  // Debug overlays reveal answers; only staff may toggle them (Shift+O)
  const { user } = useAuth();
  const isStaff = !!(user?.is_staff || user?.is_superuser);
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const gamesBase = useGamesBase();
  const location = useLocation();
  const settings = location.state || {};
  
  const level = settings.level || 1;
  const speed = settings.speed || 'slow';
  const feedbackEnabled = settings.feedback !== undefined ? settings.feedback : true;
  
  const digitInterval = SPEED_CONFIG[speed]; // ms between digit starts
  
  // Game state
  const [gamePhase, setGamePhase] = useState('intro'); // 'intro', 'listening', 'input', 'feedback', 'results'
  const [currentRound, setCurrentRound] = useState(0);
  const [sequence, setSequence] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [roundScore, setRoundScore] = useState(0);
  const [roundsData, setRoundsData] = useState([]);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [introCountdown, setIntroCountdown] = useState(3);
  const [showNumpad, setShowNumpad] = useState(false);
  const [currentDigitIndex, setCurrentDigitIndex] = useState(-1);
  
  // Debug overlay
  const [showDebug, setShowDebug] = useState(false);
  
  // Debug interval control
  const [debugInterval, setDebugInterval] = useState(SPEED_CONFIG[speed]);
  const debugIntervalRef = useRef(SPEED_CONFIG[speed]);
  
  // Track how many deletes are still available (resets to MAX_DELETES on each new digit)
  const [deletesRemaining, setDeletesRemaining] = useState(0);
  
  // Refs
  const speakingRef = useRef(false);
  const sequenceRef = useRef([]);
  const gameStartedRef = useRef(null);
  const abortRef = useRef(false);
  
  // Load Web Audio buffers on mount + cleanup on unmount
  useEffect(() => {
    warmUpAudio(); // Initialize audio context and load buffers
    return () => {
      abortRef.current = true;
      stopAllAudio();
    };
  }, []);
  
  // Debug overlay toggle (Shift + O) + interval controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isStaff && e.shiftKey && e.key === 'O') {
        setShowDebug(prev => !prev);
      }
      if (!showDebug) return;
      
      if (e.key === ']') {
        e.preventDefault();
        const newInt = debugIntervalRef.current + 50;
        setDebugInterval(newInt);
        debugIntervalRef.current = newInt;
      } else if (e.key === '[') {
        e.preventDefault();
        const newInt = Math.max(debugIntervalRef.current - 50, 0);
        setDebugInterval(newInt);
        debugIntervalRef.current = newInt;
      } else if (e.key === 'p') {
        // Preview: play digit 5 with current interval
        playDigit(5);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDebug, isStaff]);
  
  // Keyboard input for numpad
  useEffect(() => {
    if (gamePhase !== 'input') return;
    
    const handleKeyDown = (e) => {
      const key = e.key;
      if (key >= '1' && key <= '9') {
        handleNumpadPress(parseInt(key));
      } else if (key === 'Backspace' || key === 'Delete') {
        handleDelete();
      } else if (key === 'Enter') {
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gamePhase, userInput, deletesRemaining]);
  
  // Intro countdown
  useEffect(() => {
    if (gamePhase !== 'intro') return;
    
    if (introCountdown <= 0) {
      startRound();
      return;
    }
    
    const timer = setTimeout(() => {
      setIntroCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [gamePhase, introCountdown]);
  
  const startRound = useCallback(() => {
    const newSequence = generateSequence();
    setSequence(newSequence);
    sequenceRef.current = newSequence;
    setUserInput('');
    setDeletesRemaining(0);
    setShowNumpad(false);
    setCurrentDigitIndex(-1);
    setGamePhase('listening');
    speakingRef.current = true;
    abortRef.current = false;
    
    // Play digits sequentially using Web Audio API for consistent timing
    const interval = debugIntervalRef.current;
    
    const playSequence = async () => {
      // Ensure AudioContext is ready (must happen after user interaction)
      ensureAudioContext();
      // Wait for buffers if still loading
      if (!audioLoaded) await loadAllDigitBuffers();
      
      // Initial delay before starting
      await new Promise(r => setTimeout(r, 500));
      
      for (let i = 0; i < newSequence.length; i++) {
        if (abortRef.current) return;
        
        setCurrentDigitIndex(i);
        
        // Show numpad when the LAST digit starts being read
        if (i === newSequence.length - 1) {
          setShowNumpad(true);
        }
        
        await playDigit(newSequence[i]);
        
        // Wait the gap interval after each digit (except the last)
        if (i < newSequence.length - 1) {
          await new Promise(r => setTimeout(r, interval));
        }
      }
      
      if (abortRef.current) return;
      speakingRef.current = false;
      setGamePhase('input');
      setShowNumpad(true);
    };
    
    playSequence();
  }, [digitInterval, speed]);
  
  const handleNumpadPress = useCallback((digit) => {
    setUserInput(prev => prev + digit);
    setDeletesRemaining(MAX_DELETES); // Each new digit resets delete allowance
  }, []);
  
  const handleDelete = useCallback(() => {
    if (userInput.length === 0) return;
    if (deletesRemaining <= 0) return;
    
    setUserInput(prev => prev.slice(0, -1));
    setDeletesRemaining(prev => prev - 1);
  }, [userInput, deletesRemaining]);
  
  const handleSubmit = useCallback(() => {
    if (gamePhase !== 'input') return;
    
    const score = calculateScore(sequence, userInput.split('').map(Number));
    setRoundScore(score);
    
    const roundData = {
      round: currentRound + 1,
      sequence: sequence.join(''),
      sequence_length: sequence.length,
      reversed_sequence: [...sequence].reverse().join(''),
      user_input: userInput,
      score: score,
    };
    
    const newRoundsData = [...roundsData, roundData];
    setRoundsData(newRoundsData);
    
    if (feedbackEnabled) {
      setGamePhase('feedback');
    } else {
      // No feedback - move to next round or results
      if (currentRound + 1 >= TOTAL_ROUNDS) {
        finishGame(newRoundsData);
      } else {
        setCurrentRound(prev => prev + 1);
        // Small delay before next round
        setTimeout(() => {
          startRound();
        }, 500);
      }
    }
  }, [gamePhase, sequence, userInput, currentRound, roundsData, feedbackEnabled, startRound]);
  
  const handleNextRound = useCallback(() => {
    if (currentRound + 1 >= TOTAL_ROUNDS) {
      finishGame(roundsData);
    } else {
      setCurrentRound(prev => prev + 1);
      startRound();
    }
  }, [currentRound, roundsData, startRound]);
  
  const finishGame = async (finalRoundsData) => {
    setGamePhase('results');
    
    // Save to backend
    try {
      await apiClient.post('/api/courses/rms-game/runs/', {
        subject: subjectId,
        level: level,
        speed: speed,
        feedback_enabled: feedbackEnabled,
        score_multiplier: 1.0,
        rounds_data: finalRoundsData,
        started_at: gameStartedRef.current || new Date().toISOString(),
      });
    } catch (error) {
      console.error('Failed to save RMS game run:', error);
    }
  };
  
  const handleExit = () => {
    abortRef.current = true;
    stopAllAudio();
    navigate(`${gamesBase}/rms/${subjectId}`);
  };
  
  // Set game start time
  useEffect(() => {
    gameStartedRef.current = new Date().toISOString();
  }, []);
  
  // Calculate final average score
  const averageScore = roundsData.length > 0 
    ? (roundsData.reduce((sum, r) => sum + r.score, 0) / roundsData.length).toFixed(1)
    : 0;

  return (
    <div className="fixed inset-0 top-16 bg-white flex flex-col overflow-hidden" style={{ animation: 'fadeFromBlack 1.5s ease-out' }}>
      <style>{`
        @keyframes fadeFromBlack {
          0% { filter: brightness(0); }
          100% { filter: brightness(1); }
        }
      `}</style>
      
      {/* Exit button */}
      <button
        onClick={() => setShowExitConfirm(true)}
        className="absolute top-4 right-4 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg z-50"
      >
        <XMarkIcon className="w-6 h-6 text-white" />
      </button>
      
      {/* Main Game Area */}
      <div className="flex-1 flex items-center justify-center">
        
        {/* Intro Phase */}
        {gamePhase === 'intro' && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Running Memory Span</h2>
            <p className="text-gray-500 mb-2">Listen to the sequence, then enter it in reverse order</p>
            <p className="text-4xl font-bold text-gray-800 mt-8">{introCountdown > 0 ? introCountdown : 'Go!'}</p>
          </div>
        )}
        
        {/* Listening Phase - Headphones icon */}
        {gamePhase === 'listening' && !showNumpad && (
          <div className="text-center">
            <svg className="w-24 h-24 mx-auto text-gray-800" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1a9 9 0 0 0-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7a9 9 0 0 0-9-9z"/>
            </svg>
          </div>
        )}
        
        {/* Input Phase - Numpad */}
        {(gamePhase === 'input' || (gamePhase === 'listening' && showNumpad)) && (
          <div className="text-center">
            <p className="text-sm font-bold text-gray-800 mb-3">Sequence of numbers</p>
            
            {/* Input display */}
            <div className="mx-auto w-72 mb-6">
              <input
                type="text"
                value={userInput}
                readOnly
                className="w-full px-4 py-3 text-right text-lg font-mono bg-gray-100 border border-gray-300 rounded-lg text-gray-800"
                placeholder=""
              />
            </div>
            
            {/* Numpad */}
            <div className="inline-grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                <button
                  key={digit}
                  onClick={() => handleNumpadPress(digit)}
                  className="w-16 h-16 rounded-lg text-xl font-bold text-white shadow-md transition-all active:scale-95"
                  style={{ 
                    background: 'linear-gradient(180deg, #00d4ff 0%, #00a8cc 100%)',
                    border: '2px solid #0099bb'
                  }}
                >
                  {digit}
                </button>
              ))}
            </div>
            
            {/* DEL and OK buttons */}
            <div className="flex justify-center gap-2 mt-2" style={{ width: '212px', margin: '8px auto 0' }}>
              <button
                onClick={handleDelete}
                disabled={userInput.length === 0 || deletesRemaining <= 0}
                className={`w-16 h-10 rounded-lg text-sm font-bold shadow-md transition-all active:scale-95 ${
                  userInput.length > 0 && deletesRemaining > 0
                    ? 'bg-gray-200 hover:bg-gray-300 text-gray-700 border border-gray-400'
                    : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                }`}
              >
                DEL
              </button>
              <div className="w-16"></div>
              <button
                onClick={handleSubmit}
                className="w-16 h-10 rounded-lg text-sm font-bold bg-gray-200 hover:bg-gray-300 text-gray-700 border border-gray-400 shadow-md transition-all active:scale-95"
              >
                OK
              </button>
            </div>
          </div>
        )}
        
        {/* Feedback Phase */}
        {gamePhase === 'feedback' && (
          <div className="text-center max-w-md">
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-1">numbers read</p>
              <div className="mx-auto w-72">
                <input
                  type="text"
                  value={[...sequence].reverse().join('')}
                  readOnly
                  className="w-full px-4 py-3 text-right text-lg font-mono bg-gray-100 border border-gray-300 rounded-lg text-gray-800"
                />
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-1">Numbers entered</p>
              <div className="mx-auto w-72">
                <input
                  type="text"
                  value={userInput}
                  readOnly
                  className="w-full px-4 py-3 text-right text-lg font-mono bg-gray-100 border border-gray-300 rounded-lg text-gray-800"
                />
              </div>
            </div>
            
            <div className="mb-8">
              <p className="text-sm text-gray-600 mb-1">Numbers entered correctly:</p>
              <p className="text-2xl font-bold text-green-600">{roundScore}</p>
            </div>
            
            <button
              onClick={handleNextRound}
              className="px-8 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 border border-gray-400 rounded-lg font-medium transition-colors"
            >
              next Item
            </button>
          </div>
        )}
        
        {/* Results Phase */}
        {gamePhase === 'results' && (
          <div className="text-center max-w-lg w-full px-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Run Complete</h2>
            <p className="text-gray-500 mb-6">Level {level} | {speed === 'fast' ? 'Fast' : 'Slow'} Speed</p>
            
            <div className="mb-6">
              <p className="text-5xl font-bold text-emerald-600">{averageScore}</p>
              <p className="text-sm text-gray-500 mt-1">Average Score ({TOTAL_ROUNDS} rounds)</p>
            </div>
            
            {/* Round-by-round table */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-200">
                    <th className="py-2 text-left">Round</th>
                    <th className="py-2 text-center">Seq Length</th>
                    <th className="py-2 text-center">Your Input</th>
                    <th className="py-2 text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {roundsData.map((round, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 text-left text-gray-700">{round.round}</td>
                      <td className="py-2 text-center text-gray-500">{round.sequence_length}</td>
                      <td className="py-2 text-center font-mono text-gray-600 text-xs">{round.user_input || '-'}</td>
                      <td className="py-2 text-right font-bold text-emerald-600">{round.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <button
              onClick={() => navigate(`${gamesBase}/rms/${subjectId}`)}
              className="px-8 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-medium transition-colors"
            >
              Back to Setup
            </button>
          </div>
        )}
      </div>
      
      {/* Debug Overlay (Shift + O) */}
      {showDebug && (
        <div className="fixed top-12 left-4 bg-black/80 text-green-400 p-4 rounded-lg text-xs font-mono z-50 max-w-md">
          <p className="font-bold mb-2 text-white">DEBUG (Shift+O to toggle)</p>
          <p>Phase: {gamePhase}</p>
          <p>Round: {currentRound + 1}/{TOTAL_ROUNDS}</p>
          <p>Level: {level} | Speed: {speed}</p>
          <p>Feedback: {feedbackEnabled ? 'ON' : 'OFF'}</p>
          <p>Sequence: {sequence.join(', ')}</p>
          <p>Reversed: {[...sequence].reverse().join(', ')}</p>
          <p>User Input: {userInput}</p>
          <p>Deletes remaining: {deletesRemaining}</p>
          <p>Can delete: {deletesRemaining > 0 && userInput.length > 0 ? 'YES' : 'NO'}</p>
          <p className="mt-2 text-cyan-400 font-bold">Audio Settings</p>
          <p>Source: <span className="text-white">Pre-recorded WAV</span></p>
          <p>Interval (gap): <span className="text-white">{debugInterval}ms</span></p>
          <p className="mt-1 text-gray-400">[ ] interval ±50ms | p preview</p>
          <p className="mt-2 text-yellow-400">Rounds completed: {roundsData.length}</p>
          {roundsData.map((r, i) => (
            <p key={i}>R{r.round}: seq={r.sequence_length} score={r.score}</p>
          ))}
        </div>
      )}
      
      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Exit Game?</h3>
            <p className="text-gray-600 mb-4">Your progress will not be saved.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="flex-1 py-2.5 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExit}
                className="flex-1 py-2.5 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RMSGame;
